import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';
import { leadUpdatePayload } from '../utils/leadPayloads';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [lead, setLead] = useState(null);
  const [tab, setTab] = useState('overview');
  const [timeline, setTimeline] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', position: '', phone: '', email: '', contact_type: 'primary', is_primary: false });
  const [showOppForm, setShowOppForm] = useState(false);
  const [oppForm, setOppForm] = useState({ name: '', module: '', service: '', priority: 'medium', expected_value: '', expected_closing_date: '' });
  const [stages, setStages] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [sources, setSources] = useState([]);
  const [users, setUsers] = useState([]);

  const refreshLead = useCallback(() => {
    if (!companyId || !leadId) return;
    fetch(`${API}/companies/${companyId}/leads/${leadId}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setLead(d); setForm(d); })
      .catch(() => {});
  }, [companyId, leadId]);

  useEffect(() => {
    refreshLead();
    if (!companyId || !leadId) return;
    fetch(`${API}/companies/${companyId}/leads/${leadId}/timeline?per_page=100`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(d => setTimeline(Array.isArray(d.data) ? d.data : [])).catch(() => {});
    fetch(`${API}/companies/${companyId}/leads/settings/pipeline-stages`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setStages(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/companies/${companyId}/leads/sources?status=active`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setSources(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/companies/${companyId}/leads/owners`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
    // Proposals for this lead
    fetch(`${API}/companies/${companyId}/leads/proposals?lead_id=${leadId}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(d => setProposals(Array.isArray(d.data) ? d.data : Array.isArray(d) ? d : [])).catch(() => {});
    // Activities for this lead
    fetch(`${API}/companies/${companyId}/leads/activities?lead_id=${leadId}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(d => setActivities(Array.isArray(d.data) ? d.data : Array.isArray(d) ? d : [])).catch(() => {});
  }, [companyId, leadId, refreshLead]);

  const saveLead = () => {
    fetch(`${API}/companies/${companyId}/leads/${leadId}`, {
      method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(leadUpdatePayload(form, lead)),
    }).then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.message || 'Lead could not be saved.'); return d; })
      .then(d => { setLead(d); setForm(d); setEditing(false); })
      .catch(error => window.alert(error.message));
  };

  const addContact = () => {
    fetch(`${API}/companies/${companyId}/leads/${leadId}/contacts`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(contactForm),
    }).then(r => r.ok ? r.json() : null).then(() => {
      setShowContactForm(false);
      setContactForm({ name: '', position: '', phone: '', email: '', contact_type: 'primary', is_primary: false });
      refreshLead();
    });
  };

  const deleteContact = (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    fetch(`${API}/companies/${companyId}/leads/${leadId}/contacts/${contactId}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    }).then(() => refreshLead());
  };

  const createOpp = () => {
    const data = { ...oppForm, lead_id: parseInt(leadId), pipeline_stage_id: stages[0]?.id };
    fetch(`${API}/companies/${companyId}/leads/opportunities`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(r => r.ok ? r.json() : null).then(() => {
      setShowOppForm(false);
      setOppForm({ name: '', module: '', service: '', priority: 'medium', expected_value: '', expected_closing_date: '' });
      refreshLead();
    });
  };

  const archiveLead = () => {
    if (!window.confirm('Archive this lead?')) return;
    fetch(`${API}/companies/${companyId}/leads/${leadId}/archive`, {
      method: 'POST', headers: getAuthHeaders(),
    }).then(() => refreshLead());
  };

  const restoreLead = () => {
    fetch(`${API}/companies/${companyId}/leads/${leadId}/restore`, {
      method: 'POST', headers: getAuthHeaders(),
    }).then(() => refreshLead());
  };

  if (!lead) return <div style={{ padding: 32, color: '#6b7280' }}>Loading...</div>;

  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
  const selectStyle = { ...inputStyle };
  const labelStyle = { fontSize: 12, color: '#6b7280', marginBottom: 4, display: 'block', fontWeight: 600 };
  const priorityColors = { high: '#ef4444', medium: '#f59e0b', low: '#6b7280' };
  const statusColors = { new: '#3b82f6', contacted: '#8b5cf6', qualified: '#22c55e', unqualified: '#6b7280', active: '#10b981', inactive: '#9ca3af', won: '#22c55e', lost: '#ef4444', archived: '#d1d5db' };
  const tabList = ['overview', 'contacts', 'opportunities', 'activities', 'proposals', 'timeline'];
  const tabStyle = (t) => ({ padding: '10px 20px', border: 'none', borderBottom: tab === t ? '3px solid #6366f1' : '3px solid transparent', background: 'none', fontWeight: tab === t ? 700 : 400, color: tab === t ? '#6366f1' : '#6b7280', cursor: 'pointer', fontSize: 14 });
  const actionColors = { created: '#22c55e', updated: '#3b82f6', deleted: '#ef4444', stage_changed: '#8b5cf6', status_changed: '#f59e0b', owner_changed: '#6366f1', archived: '#9ca3af', restored: '#10b981', won: '#22c55e', lost: '#ef4444' };

  return (
    <div style={{ padding: 32, maxWidth: 1300 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <button onClick={() => navigate('/company/leads/directory')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 14, marginBottom: 8, padding: 0 }}>← Back to Directory</button>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
            {lead.lead_type === 'company' ? '🏢' : '👤'} {lead.lead_name}
            <span style={{ fontSize: 14, color: '#9ca3af', fontWeight: 400, marginLeft: 8 }}>({lead.lead_code})</span>
          </h2>
          {lead.company_name && lead.lead_type === 'individual' && <div style={{ fontSize: 14, color: '#6b7280' }}>at {lead.company_name}</div>}
          {lead.is_archived && <span style={{ fontSize: 12, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>ARCHIVED</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ padding: '4px 14px', borderRadius: 12, fontSize: 13, fontWeight: 600, color: '#fff', background: priorityColors[lead.priority] }}>{lead.priority}</span>
          <span style={{ padding: '4px 14px', borderRadius: 12, fontSize: 13, fontWeight: 600, color: '#fff', background: statusColors[lead.status] }}>{lead.status}</span>
          {!lead.is_archived ? (
            <button onClick={archiveLead} style={{ border: '1px solid #f59e0b', background: '#fff', color: '#f59e0b', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Archive</button>
          ) : (
            <button onClick={restoreLead} style={{ border: '1px solid #22c55e', background: '#fff', color: '#22c55e', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Restore</button>
          )}
        </div>
      </div>

      {/* Tags */}
      {Array.isArray(lead.tags) && lead.tags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {lead.tags.map((t, i) => (
            <span key={i} style={{ padding: '2px 10px', borderRadius: 12, background: '#eef2ff', color: '#4f46e5', fontSize: 12, fontWeight: 500 }}>{t}</span>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: 24, display: 'flex', gap: 4, overflowX: 'auto' }}>
        {tabList.map(t => (
          <button key={t} onClick={() => setTab(t)} style={tabStyle(t)}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
          <div>
            <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ fontWeight: 600 }}>Lead Information</h3>
                {!editing ? (
                  <button onClick={() => setEditing(true)} style={{ border: '1px solid #6366f1', background: '#fff', color: '#6366f1', padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>Edit</button>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={saveLead} style={{ border: 'none', background: '#6366f1', color: '#fff', padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Save</button>
                    <button onClick={() => { setEditing(false); setForm(lead); }} style={{ border: '1px solid #d1d5db', background: '#fff', padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {[
                  ['Lead Name', 'lead_name'], ['Company', 'company_name'], ['Email', 'email'], ['Phone', 'phone'],
                  ['Website', 'website'], ['Country', 'country'],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label style={labelStyle}>{label}</label>
                    {editing ? <input style={inputStyle} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} />
                      : <div style={{ fontWeight: 500, fontSize: 14 }}>{lead[key] || '—'}</div>}
                  </div>
                ))}
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Address</label>
                  {editing ? <input style={inputStyle} value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} />
                    : <div style={{ fontWeight: 500, fontSize: 14 }}>{lead.address || '—'}</div>}
                </div>
                {editing && (
                  <>
                    <div>
                      <label style={labelStyle}>Priority</label>
                      <select style={selectStyle} value={form.priority || 'medium'} onChange={e => setForm({ ...form, priority: e.target.value })}>
                        <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Status</label>
                      <select style={selectStyle} value={form.status || 'new'} onChange={e => setForm({ ...form, status: e.target.value })}>
                        {['new','contacted','qualified','unqualified','active','inactive'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Source</label>
                      <select style={selectStyle} value={form.lead_source_id || ''} onChange={e => setForm({ ...form, lead_source_id: e.target.value })}>
                        <option value="">None</option>
                        {sources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Owner</label>
                      <select style={selectStyle} value={form.assigned_owner_id || ''} onChange={e => setForm({ ...form, assigned_owner_id: e.target.value })}>
                        <option value="">Unassigned</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Notes */}
            <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
              <h3 style={{ fontWeight: 600, marginBottom: 12 }}>Notes</h3>
              {editing ? (
                <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} />
              ) : (
                <div style={{ fontSize: 14, color: lead.notes ? '#374151' : '#9ca3af', whiteSpace: 'pre-wrap' }}>{lead.notes || 'No notes added'}</div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div>
            <div style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.08)', marginBottom: 16 }}>
              <h4 style={{ fontWeight: 600, marginBottom: 12 }}>Details</h4>
              <div style={{ fontSize: 13, lineHeight: 2 }}>
                <div><span style={{ color: '#6b7280' }}>Type:</span> <strong>{lead.lead_type === 'company' ? '🏢 Company' : '👤 Individual'}</strong></div>
                <div><span style={{ color: '#6b7280' }}>Source:</span> <strong>{lead.source?.name || '—'}</strong></div>
                <div><span style={{ color: '#6b7280' }}>Owner:</span> <strong>{lead.owner?.name || 'Unassigned'}</strong></div>
                <div><span style={{ color: '#6b7280' }}>Created by:</span> <strong>{lead.creator?.name || '—'}</strong></div>
                <div><span style={{ color: '#6b7280' }}>Created:</span> <strong>{new Date(lead.created_at).toLocaleDateString()}</strong></div>
                <div><span style={{ color: '#6b7280' }}>Last Activity:</span> <strong>{lead.last_activity_at ? new Date(lead.last_activity_at).toLocaleDateString() : 'Never'}</strong></div>
              </div>
            </div>

            <div style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.08)', marginBottom: 16 }}>
              <h4 style={{ fontWeight: 600, marginBottom: 12 }}>Summary</h4>
              <div style={{ fontSize: 13, lineHeight: 2 }}>
                <div>Contacts: <strong>{lead.contacts?.length || 0}</strong></div>
                <div>Opportunities: <strong>{lead.opportunities?.length || 0}</strong></div>
                <div>Active Opps: <strong>{(lead.opportunities || []).filter(o => o.status === 'active').length}</strong></div>
                <div>Pipeline Value: <strong style={{ color: '#10b981' }}>${Number((lead.opportunities || []).filter(o => o.status === 'active').reduce((s, o) => s + Number(o.expected_value || 0), 0)).toLocaleString()}</strong></div>
                {lead.next_follow_up && (
                  <div style={{ color: '#f59e0b' }}>Next Follow-Up: <strong>{new Date(lead.next_follow_up.scheduled_at).toLocaleDateString()}</strong></div>
                )}
              </div>
            </div>

            {/* Primary Contact Quick View */}
            {lead.contacts && lead.contacts.find(c => c.is_primary) && (
              <div style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
                <h4 style={{ fontWeight: 600, marginBottom: 12 }}>Primary Contact</h4>
                {(() => {
                  const pc = lead.contacts.find(c => c.is_primary);
                  return (
                    <div style={{ fontSize: 13 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{pc.name}</div>
                      {pc.position && <div style={{ color: '#6b7280', marginBottom: 4 }}>{pc.position}</div>}
                      {pc.email && <div>📧 {pc.email}</div>}
                      {pc.phone && <div>📞 {pc.phone}</div>}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CONTACTS TAB ── */}
      {tab === 'contacts' && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 600 }}>Contacts ({lead.contacts?.length || 0})</h3>
            <button onClick={() => setShowContactForm(true)} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>+ Add Contact</button>
          </div>
          {(lead.contacts || []).length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>No contacts added yet</div>}
          {(lead.contacts || []).map(c => (
            <div key={c.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {c.name}
                  {c.is_primary && <span style={{ fontSize: 11, background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: 8, marginLeft: 8 }}>Primary</span>}
                  <span style={{ fontSize: 11, background: '#f3f4f6', color: '#6b7280', padding: '2px 8px', borderRadius: 8, marginLeft: 4 }}>{c.contact_type}</span>
                </div>
                {c.position && <div style={{ fontSize: 13, color: '#6b7280' }}>{c.position}</div>}
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  {c.email && <span>📧 {c.email}</span>}
                  {c.phone && <span style={{ marginLeft: c.email ? 16 : 0 }}>📞 {c.phone}</span>}
                </div>
                {c.notes && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{c.notes}</div>}
              </div>
              <button onClick={() => deleteContact(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16, padding: 4 }} title="Delete contact">🗑️</button>
            </div>
          ))}
          {showContactForm && (
            <div style={{ border: '2px solid #6366f1', borderRadius: 8, padding: 20, marginTop: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={labelStyle}>Name *</label><input style={inputStyle} value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} /></div>
                <div><label style={labelStyle}>Position</label><input style={inputStyle} value={contactForm.position} onChange={e => setContactForm({ ...contactForm, position: e.target.value })} /></div>
                <div><label style={labelStyle}>Email</label><input style={inputStyle} type="email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} /></div>
                <div><label style={labelStyle}>Phone</label><input style={inputStyle} value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} /></div>
                <div>
                  <label style={labelStyle}>Contact Type</label>
                  <select style={selectStyle} value={contactForm.contact_type} onChange={e => setContactForm({ ...contactForm, contact_type: e.target.value })}>
                    {['primary','secondary','billing','technical','other'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={contactForm.is_primary} onChange={e => setContactForm({ ...contactForm, is_primary: e.target.checked })} />
                    Set as primary contact
                  </label>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={addContact} disabled={!contactForm.name} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13, opacity: contactForm.name ? 1 : 0.5 }}>Save Contact</button>
                <button onClick={() => setShowContactForm(false)} style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── OPPORTUNITIES TAB ── */}
      {tab === 'opportunities' && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 600 }}>Opportunities ({lead.opportunities?.length || 0})</h3>
            <button onClick={() => setShowOppForm(true)} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>+ New Opportunity</button>
          </div>

          {/* Opportunity summary bar */}
          {(lead.opportunities || []).length > 0 && (
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, padding: '12px 16px', background: '#f9fafb', borderRadius: 8 }}>
              <div style={{ fontSize: 13 }}>Active: <strong>{(lead.opportunities || []).filter(o => o.status === 'active').length}</strong></div>
              <div style={{ fontSize: 13 }}>Won: <strong style={{ color: '#22c55e' }}>{(lead.opportunities || []).filter(o => o.status === 'won').length}</strong></div>
              <div style={{ fontSize: 13 }}>Lost: <strong style={{ color: '#ef4444' }}>{(lead.opportunities || []).filter(o => o.status === 'lost').length}</strong></div>
              <div style={{ fontSize: 13, marginLeft: 'auto' }}>Total Pipeline: <strong style={{ color: '#10b981' }}>${Number((lead.opportunities || []).filter(o => o.status === 'active').reduce((s, o) => s + Number(o.expected_value || 0), 0)).toLocaleString()}</strong></div>
            </div>
          )}

          {(lead.opportunities || []).length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>No opportunities yet</div>}
          {(lead.opportunities || []).map(o => (
            <div key={o.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 10, cursor: 'pointer' }}
              onMouseOver={e => e.currentTarget.style.borderColor = '#6366f1'} onMouseOut={e => e.currentTarget.style.borderColor = '#e5e7eb'}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{o.name}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{[o.module, o.service].filter(Boolean).join(' / ') || '—'}</div>
                  {o.expected_closing_date && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>Close: {o.expected_closing_date}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, color: '#10b981', fontSize: 16 }}>{o.expected_value ? '$' + Number(o.expected_value).toLocaleString() : '—'}</div>
                  <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: '#fff', background: statusColors[o.status] || '#6b7280' }}>{o.status}</span>
                  {o.pipeline_stage && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Stage: {o.pipeline_stage.name}</div>}
                  {o.probability && <div style={{ fontSize: 12, color: '#9ca3af' }}>{o.probability}% probability</div>}
                </div>
              </div>
            </div>
          ))}

          {showOppForm && (
            <div style={{ border: '2px solid #6366f1', borderRadius: 8, padding: 20, marginTop: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Opportunity Name *</label><input style={inputStyle} value={oppForm.name} onChange={e => setOppForm({ ...oppForm, name: e.target.value })} /></div>
                <div><label style={labelStyle}>Module</label><input style={inputStyle} value={oppForm.module} onChange={e => setOppForm({ ...oppForm, module: e.target.value })} /></div>
                <div><label style={labelStyle}>Service</label><input style={inputStyle} value={oppForm.service} onChange={e => setOppForm({ ...oppForm, service: e.target.value })} /></div>
                <div><label style={labelStyle}>Expected Value</label><input style={inputStyle} type="number" value={oppForm.expected_value} onChange={e => setOppForm({ ...oppForm, expected_value: e.target.value })} /></div>
                <div><label style={labelStyle}>Expected Closing</label><input style={inputStyle} type="date" value={oppForm.expected_closing_date} onChange={e => setOppForm({ ...oppForm, expected_closing_date: e.target.value })} /></div>
                <div><label style={labelStyle}>Priority</label>
                  <select style={selectStyle} value={oppForm.priority} onChange={e => setOppForm({ ...oppForm, priority: e.target.value })}>
                    <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                  </select>
                </div>
                {stages.length > 0 && (
                  <div><label style={labelStyle}>Pipeline Stage</label>
                    <select style={selectStyle} value={oppForm.pipeline_stage_id || stages[0]?.id || ''} onChange={e => setOppForm({ ...oppForm, pipeline_stage_id: e.target.value })}>
                      {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={createOpp} disabled={!oppForm.name} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13, opacity: oppForm.name ? 1 : 0.5 }}>Create</button>
                <button onClick={() => setShowOppForm(false)} style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ACTIVITIES TAB ── */}
      {tab === 'activities' && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 600 }}>Activities & Follow-Ups</h3>
            <button onClick={() => navigate('/company/leads/activities')} style={{ border: '1px solid #6366f1', background: '#fff', color: '#6366f1', padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>View All Activities →</button>
          </div>
          {activities.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>No activities for this lead</div>
          ) : activities.map(a => {
            const isOverdue = a.scheduled_at && new Date(a.scheduled_at) < new Date() && ['scheduled','in_progress'].includes(a.status);
            return (
              <div key={a.id} style={{ borderLeft: `3px solid ${isOverdue ? '#ef4444' : a.status === 'completed' ? '#22c55e' : '#6366f1'}`, padding: '10px 16px', marginBottom: 8, background: '#f9fafb', borderRadius: '0 8px 8px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{a.title || a.activity_type?.name || 'Activity'}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                      {a.scheduled_at && <span>📅 {new Date(a.scheduled_at).toLocaleDateString()}</span>}
                      {a.assigned_user && <span style={{ marginLeft: 12 }}>👤 {a.assigned_user.name}</span>}
                    </div>
                    {a.notes && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{a.notes}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {isOverdue && <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>OVERDUE</span>}
                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: '#fff',
                      background: a.status === 'completed' ? '#22c55e' : a.status === 'cancelled' ? '#9ca3af' : a.status === 'in_progress' ? '#3b82f6' : '#f59e0b' }}>
                      {a.status}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── PROPOSALS TAB ── */}
      {tab === 'proposals' && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 600 }}>Proposals</h3>
            <button onClick={() => navigate('/company/leads/proposals')} style={{ border: '1px solid #6366f1', background: '#fff', color: '#6366f1', padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>View All Proposals →</button>
          </div>
          {proposals.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>No proposals for this lead</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['#', 'Title', 'Opportunity', 'Status', 'Total Value', 'Created', 'Valid Until'].map((h, i) => (
                      <th key={i} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 13 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {proposals.map(p => {
                    const pStatusColors = { draft: '#9ca3af', awaiting_approval: '#f59e0b', sent: '#3b82f6', awaiting_response: '#6366f1', accepted: '#22c55e', rejected: '#ef4444', expired: '#9ca3af' };
                    return (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '10px 14px', color: '#6366f1', fontWeight: 500 }}>{p.proposal_number}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 600 }}>{p.title}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13 }}>{p.opportunity?.name || '—'}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: '#fff', background: pStatusColors[p.status] || '#6b7280' }}>
                            {(p.status || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px', fontWeight: 600 }}>${Number(p.total_value || 0).toLocaleString()}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#9ca3af' }}>{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#9ca3af' }}>{p.valid_until || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TIMELINE TAB ── */}
      {tab === 'timeline' && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
          <h3 style={{ fontWeight: 600, marginBottom: 16 }}>Activity Timeline</h3>
          {timeline.length === 0 ? (
            <div style={{ color: '#9ca3af', padding: 20, textAlign: 'center' }}>No history yet</div>
          ) : timeline.map(t => (
            <div key={t.id} style={{ borderLeft: '3px solid #e5e7eb', paddingLeft: 16, paddingBottom: 16, marginLeft: 8, position: 'relative' }}>
              <div style={{ position: 'absolute', left: -7, top: 0, width: 12, height: 12, borderRadius: '50%', background: actionColors[t.action] || '#e5e7eb', border: '2px solid #fff' }} />
              <div style={{ fontSize: 12, color: '#9ca3af' }}>{new Date(t.created_at).toLocaleString()}</div>
              <div style={{ fontWeight: 500, marginTop: 2 }}>{t.description}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                by {t.user?.name || 'System'} •
                <span style={{ padding: '1px 6px', borderRadius: 8, fontSize: 11, fontWeight: 600, color: '#fff', background: actionColors[t.action] || '#6b7280', marginLeft: 4 }}>
                  {t.action?.replace(/_/g, ' ')}
                </span>
              </div>
              {t.old_values && (
                <details style={{ fontSize: 12, marginTop: 4 }}>
                  <summary style={{ cursor: 'pointer', color: '#6366f1' }}>View changes</summary>
                  <div style={{ marginTop: 4, background: '#f9fafb', padding: 8, borderRadius: 4 }}>
                    {Object.keys(typeof t.old_values === 'string' ? JSON.parse(t.old_values) : (t.old_values || {})).map(k => {
                      const oldVals = typeof t.old_values === 'string' ? JSON.parse(t.old_values) : t.old_values;
                      const newVals = typeof t.new_values === 'string' ? JSON.parse(t.new_values) : (t.new_values || {});
                      return (
                        <div key={k} style={{ marginBottom: 2 }}>
                          <strong>{k}:</strong> <span style={{ color: '#ef4444', textDecoration: 'line-through' }}>{String(oldVals[k] ?? '')}</span> → <span style={{ color: '#22c55e' }}>{String(newVals[k] ?? '')}</span>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
